/**
 * Redux slice for incident field analysis
 * Handles extracted fields, pending fields, and field animations
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ===== TYPES =====

export type FieldValue = string | string[];
export type IncidentFields = Partial<Record<string, FieldValue>>;

interface AnalysisState {
  // Extracted fields with values
  extractedFields: IncidentFields;

  // Fields that are currently pending (unknown/default values)
  pendingFields: Set<string>;

  // Fields that have been extracted at least once (for sticky behavior)
  permanentlyExtractedFields: Set<string>;

  // Analysis status
  isAnalyzing: boolean;
  lastAnalysisTime: number | null;
  analysisError: string | null;
}

// ===== INITIAL STATE =====

const initialState: AnalysisState = {
  extractedFields: {},
  pendingFields: new Set(),
  permanentlyExtractedFields: new Set(),
  isAnalyzing: false,
  lastAnalysisTime: null,
  analysisError: null,
};

// ===== SLICE =====

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    updateFields: (state, action: PayloadAction<IncidentFields>) => {
      const newFields = action.payload;
      
      // Update extracted fields
      state.extractedFields = {
        ...state.extractedFields,
        ...newFields,
      };
      
      state.lastAnalysisTime = Date.now();
    },
    
    updateFieldValue: (state, action: PayloadAction<{ field: string; value: FieldValue }>) => {
      const { field, value } = action.payload;
      state.extractedFields[field] = value;
    },

    setPendingFields: (state, action: PayloadAction<string[]>) => {
      state.pendingFields = new Set(action.payload);
    },

    addPendingField: (state, action: PayloadAction<string>) => {
      state.pendingFields.add(action.payload);
    },

    removePendingField: (state, action: PayloadAction<string>) => {
      state.pendingFields.delete(action.payload);
    },

    markFieldAsExtracted: (state, action: PayloadAction<string>) => {
      const field = action.payload;
      state.permanentlyExtractedFields.add(field);
      state.pendingFields.delete(field);
    },
    
    setAnalyzing: (state, action: PayloadAction<boolean>) => {
      state.isAnalyzing = action.payload;
      if (action.payload) {
        state.analysisError = null;
      }
    },
    
    setAnalysisError: (state, action: PayloadAction<string>) => {
      state.analysisError = action.payload;
      state.isAnalyzing = false;
    },
    
    clearAnalysisError: (state) => {
      state.analysisError = null;
    },
    
    resetAnalysis: () => {
      return initialState;
    },
  },
});

export const {
  updateFields,
  updateFieldValue,
  setPendingFields,
  addPendingField,
  removePendingField,
  markFieldAsExtracted,
  setAnalyzing,
  setAnalysisError,
  clearAnalysisError,
  resetAnalysis,
} = analysisSlice.actions;

export default analysisSlice.reducer;
